import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../context/AuthContext";
import { canAccessCrm } from "../../lib/userAccess";
import { canPlaceTelCall } from "../../lib/utils";
import { core } from "../../lib/api";
import { Modal } from "../../components/ui/modal";
import { SimCallLogModal } from "./sim-call-log-modal";
import type { Lead } from "./types";

type Request = { id: string; lead: Lead; expires_at: string };

export function SimCallReceiver() {
  const { user } = useAuth();
  const cache = useQueryClient();
  const enabled = canAccessCrm(user) && canPlaceTelCall();
  const [request, setRequest] = useState<Request | null>(null);
  const [claimed, setClaimed] = useState(false);
  const [logging, setLogging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!enabled || request) return;
    let active = true;
    const poll = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const requests = await core<Request[]>("/api/v1/crm/sim-requests");
        if (active && requests[0]) { setRequest(requests[0]); setClaimed(false); setError(""); }
      } catch { /* Retry when the connection returns. */ }
    };
    void poll();
    const timer = window.setInterval(poll, 5000);
    document.addEventListener("visibilitychange", poll);
    return () => { active = false; window.clearInterval(timer); document.removeEventListener("visibilitychange", poll); };
  }, [enabled, request]);
  if (!enabled || !request) return null;
  const close = () => { setRequest(null); setLogging(false); setClaimed(false); };
  const claim = async (dismiss = false) => {
    setBusy(true);
    try {
      await core(`/api/v1/crm/sim-requests/${request.id}/claim`, { method: "POST" });
      if (dismiss) close(); else setClaimed(true);
    } catch (e) { setError(e instanceof Error ? e.message : "Unable to accept call request"); }
    finally { setBusy(false); }
  };
  if (logging) return <SimCallLogModal lead={request.lead} autoDialed onClose={close} onLog={() => { void cache.invalidateQueries({ queryKey: ["crm-contact-calls"] }); void cache.invalidateQueries({ queryKey: ["crm", "leads"] }); close(); }} />;
  return <Modal title="Call from your phone" onClose={() => { if (claimed || error) close(); else void claim(true); }}>
    <p className="text-lg font-semibold">{request.lead.name || "Unnamed lead"}</p>
    {request.lead.phone && <p className="mt-2">{request.lead.phone}</p>}
    <p className="mt-2 text-sm text-ink-3">Requested from your dashboard. Open your phone dialer to make this SIM call.</p>
    {claimed ? (request.lead.phone
      ? <a className="mt-4 inline-block rounded-lg bg-brand px-4 py-2 text-white" href={`tel:${request.lead.phone.replace(/[^+\d]/g, "")}`} onClick={() => setLogging(true)}>Open phone dialer</a>
      : <button className="mt-4 rounded-lg bg-brand px-4 py-2 text-white" onClick={() => setLogging(true)}>Log this call</button>
    ) : <button disabled={busy} onClick={() => void claim()} className="mt-4 rounded-lg bg-brand px-4 py-2 text-white disabled:opacity-50">{busy ? "Accepting…" : "Accept call request"}</button>}
    {error && <p role="alert" className="mt-3 text-red-600">{error}</p>}
  </Modal>;
}
